import React, { useMemo, useState } from 'react';
import { RotateCcwIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { toast } from '../../components/ui/Toast';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { daysUntilPurge, emptyTrash, purgeLead, restoreLead } from '../../services/leadService';
import { restoreForm } from '../../services/formService';
import { restoreLanding } from '../../services/landingService';
import { formatDate, fullName } from '../../utils/format';
import { Lead } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';

/**
 * Papelera de la organización. Los elementos usan borrado lógico y se
 * conservan 30 días antes de eliminarse definitivamente.
 */
export function Trash() {
  const {
    user,
    hasPermission
  } = useAuth();
  const {
    organizationId,
    trashedLeads,
    trashedForms,
    trashedLandings
  } = useWorkspace();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmPurge, setConfirmPurge] = useState<Lead | 'seleccion' | 'todo' | null>(null);
  const [working, setWorking] = useState(false);
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const canDelete = hasPermission('leads.delete');
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return trashedLeads;
    return trashedLeads.filter(lead => [lead.firstName, lead.lastName, lead.company, lead.email].join(' ').toLowerCase().includes(term));
  }, [trashedLeads, search]);
  async function confirmAction() {
    setWorking(true);
    try {
      if (confirmPurge === 'todo') await emptyTrash(organizationId);else if (confirmPurge === 'seleccion') {
        for (const id of selected) await purgeLead(id);
        setSelected([]);
      } else if (confirmPurge) await purgeLead(confirmPurge.id);
      toast.success('Eliminado definitivamente');
      setConfirmPurge(null);
    } catch (error) {
      toast.error('No se ha podido eliminar', {
        description: error instanceof Error ? error.message : undefined
      });
    } finally {
      setWorking(false);
    }
  }

  /** Devuelve uno o varios leads al listado activo con sus datos intactos. */
  async function restore(ids: string[]) {
    try {
      for (const id of ids) await restoreLead(id, authorName);
      setSelected(prev => prev.filter(id => !ids.includes(id)));
      toast.success(ids.length === 1 ? 'Lead restaurado' : 'Leads restaurados', {
        description: 'Vuelven a estar en Leads y en el embudo.'
      });
    } catch (error) {
      toast.error('No se ha podido restaurar', {
        description: error instanceof Error ? error.message : undefined
      });
    }
  }
  const empty = trashedLeads.length === 0 && trashedForms.length === 0 && trashedLandings.length === 0;
  return <>
      <PageHeader title="Papelera" description="Los elementos eliminados se conservan 30 días antes de borrarse definitivamente." actions={canDelete && trashedLeads.length > 0 ? <Button variant="destructive" onClick={() => setConfirmPurge('todo')} data-id="element-1930" data-mp="30hbmam4tynhm">
              Vaciar papelera
            </Button> : undefined} data-id="element-1929" data-mp="3t5pkp2ztzltp" />

      {empty ? <EmptyState icon={<Trash2Icon className="h-5 w-5" data-id="element-1932" data-mp="35go956vpbuk7" />} title="La papelera está vacía" description="Aquí aparecerán los leads, formularios y landings que elimines, por si necesitas recuperarlos." data-id="element-1931" data-mp="35gxwsnt9xpmq" /> : <Tabs defaultValue="leads" data-id="element-1933" data-mp="3f7sk7vi9av9r">
          <TabsList data-id="element-1934" data-mp="3f80urgqwpohs">
            <TabsTrigger value="leads" data-id="element-1935" data-mp="2hrbzk5wgpkhd">Leads ({trashedLeads.length})</TabsTrigger>
            <TabsTrigger value="formularios" data-id="element-1936" data-mp="2mpwz033w404r">
              Formularios ({trashedForms.length})
            </TabsTrigger>
            <TabsTrigger value="landings" data-id="element-1937" data-mp="s96qabz0n8vo">
              Landings ({trashedLandings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads" data-id="element-1938" data-mp="o9o297bwehom">
            <div className="relative mb-4 max-w-md" data-id="element-1939" data-mp="11zhwy6a33w69">
              <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" data-id="element-1940" data-mp="1212p5p8xf30y" />
              <input type="search" aria-label="Buscar en la papelera" placeholder="Buscar por nombre, empresa o email" value={search} onChange={e => setSearch(e.target.value)} className="h-11 w-full rounded-xl border border-border bg-background pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1941" data-mp="1bxfhojsqjau4" />
            </div>

            {selected.length > 0 && canDelete && <div className="mb-4 flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1942" data-mp="3mq8u98u4v6qp">
                <p className="text-sm font-medium text-foreground" data-id="element-1943" data-mp="1x83b8n6vxgrt">
                  {selected.length} seleccionados
                </p>
                <div className="flex flex-col gap-2 sm:flex-row" data-id="element-1944" data-mp="2276hgaylpt1d">
                  <Button variant="secondary" onClick={() => void restore(selected)} data-id="element-1945" data-mp="1oh30nzihqrjb">
                    Restaurar
                  </Button>
                  <Button variant="destructive" onClick={() => setConfirmPurge('seleccion')} data-id="element-1946" data-mp="1sg23neknwe84">
                    Eliminar definitivamente
                  </Button>
                </div>
              </div>}

            <ul className="grid gap-3" data-id="element-1947" data-mp="1jf3vck36xkjq">
              {filtered.map(lead => <li key={lead.id} className="flex flex-col gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm sm:flex-row sm:items-center" data-id="element-1948" data-mp="15igxmf3eyqcr">
                  {canDelete && <Checkbox id={`trash-${lead.id}`} label="" aria-label={`Seleccionar ${lead.firstName}`} checked={selected.includes(lead.id)} onChange={e => setSelected(prev => e.target.checked ? [...prev, lead.id] : prev.filter(id => id !== lead.id))} data-id="element-1949" data-mp="2zvq1nrd6xjnm" />}
                  <div className="min-w-0 flex-1" data-id="element-1950" data-mp="gpw10nafz7st">
                    <p className="truncate font-medium text-foreground" data-id="element-1951" data-mp="bozt7ymyz1i4">
                      {fullName(lead.firstName, lead.lastName)}
                    </p>
                    <p className="truncate text-sm text-muted-foreground" data-id="element-1952" data-mp="7pr3d4v1vnru">
                      {lead.company} · {lead.email}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground" data-id="element-1953" data-mp="ze0wl7gy3mgj">
                      Eliminado por {lead.deletedBy ?? 'Sistema'} el{' '}
                      {formatDate(lead.deletedAt ?? '')} · Se eliminará
                      definitivamente en {daysUntilPurge(lead.deletedAt)} días
                    </p>
                  </div>
                  <div className="flex flex-none gap-2" data-id="element-1954" data-mp="lll1csuacsq6">
                    <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<RotateCcwIcon className="h-4 w-4" data-id="element-1956" data-mp="1ecmiv0lgb0cv" />} onClick={() => void restore([lead.id])} data-id="element-1955" data-mp="1j44ldt34h8zy">
                      Restaurar
                    </Button>
                    {canDelete && <Button variant="destructive" size="sm" className="min-h-[44px]" onClick={() => setConfirmPurge(lead)} data-id="element-1957" data-mp="1lpu941clpsn1">
                        Eliminar
                      </Button>}
                  </div>
                </li>)}
              {filtered.length === 0 && <li className="rounded-2xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-id="element-1958" data-mp="1zo20ltqvc1qt">
                  No hay leads en la papelera.
                </li>}
            </ul>
          </TabsContent>

          <TabsContent value="formularios" data-id="element-1959" data-mp="29jdigkxkpp2s">
            <ul className="grid gap-3" data-id="element-1960" data-mp="2ebwu3ccpaa4y">
              {trashedForms.map(form => <li key={form.id} className="flex items-center gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm" data-id="element-1961" data-mp="2jb9pwmljurjo">
                  <div className="min-w-0 flex-1" data-id="element-1962" data-mp="2ndmlii6l192q">
                    <p className="truncate font-medium text-foreground" data-id="element-1963" data-mp="2nb0csf7ygnix">{form.name}</p>
                    <p className="text-xs text-muted-foreground" data-id="element-1964" data-mp="2sc7o2o9b44uc">
                      Se eliminará definitivamente en {daysUntilPurge(form.deletedAt)}{' '}
                      días
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<RotateCcwIcon className="h-4 w-4" data-id="element-1966" data-mp="3nizoii8gneyp" />} onClick={() => restoreForm(form.id)} data-id="element-1965" data-mp="3nksml950cqv3">
                    Restaurar
                  </Button>
                </li>)}
              {trashedForms.length === 0 && <li className="rounded-2xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-id="element-1967" data-mp="2uuanej7en9my">
                  No hay formularios eliminados.
                </li>}
            </ul>
          </TabsContent>

          <TabsContent value="landings" data-id="element-1968" data-mp="2zuy9cqgs10ab">
            <ul className="grid gap-3" data-id="element-1969" data-mp="2ztwvke8dlzow">
              {trashedLandings.map(landing => <li key={landing.id} className="flex items-center gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm" data-id="element-1970" data-mp="2c4vlynnt4ii1">
                  <div className="min-w-0 flex-1" data-id="element-1971" data-mp="276ke2ih2oi2g">
                    <p className="truncate font-medium text-foreground" data-id="element-1972" data-mp="moi5uc853n9b">
                      {landing.name}
                    </p>
                    <p className="text-xs text-muted-foreground" data-id="element-1973" data-mp="rh2wj4vr1k8m">
                      Se eliminará definitivamente en{' '}
                      {daysUntilPurge(landing.deletedAt)} días
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<RotateCcwIcon className="h-4 w-4" data-id="element-1975" data-mp="15g2k097izt37" />} onClick={() => restoreLanding(landing.id)} data-id="element-1974" data-mp="15f9ns2670lzb">
                    Restaurar
                  </Button>
                </li>)}
              {trashedLandings.length === 0 && <li className="rounded-2xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-id="element-1976" data-mp="wgo05slv6gro">
                  No hay landings eliminadas.
                </li>}
            </ul>
          </TabsContent>
        </Tabs>}

      <ConfirmDialog open={Boolean(confirmPurge)} title="Eliminar definitivamente" description="Esta acción eliminará permanentemente el lead y toda su información. No se podrá recuperar." confirmLabel="Eliminar definitivamente" loading={working} onCancel={() => setConfirmPurge(null)} onConfirm={confirmAction} data-id="element-1977" data-mp="3l6npnvlb1h5k" />
    </>;
}